<script>
/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkListFilter from '@components/v2/filter/AkListFilter.vue';
import AkInputFilter from '@components/v2/filter/AkInputFilter.vue';
import AkAllFilter from '@components/v2/filter/AkAllFilter.vue';
import EmployeeCreateSidebar from '@views/employee/EmployeeCreateSidebar.vue';
import AkInputSwitch from '@components/v2/input/AkInputSwitch.vue';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import sidebarMixin from '@mixins/sidebarMixin';
import companyMixin from '@mixins/companyMixin';
import poleMixin from '@mixins/poleMixin';
import roleMixin from '@mixins/roleMixin';
import filterListMixin from '@mixins/filterListMixin';
import messageMixin from '@mixins/messageMixin';
import {PermissionMap} from '@mixins/const/permissionConst';
import civilitiesConst from '@mixins/const/civilitiesConst';
import roleKeysConst from '@mixins/const/roleKeysConst';

/* SERVICES */
import employeeService from '@services/employeeService';

export default {
  components: {
    EmployeeCreateSidebar,
    AkAllFilter,
    AkInputFilter,
    AkListFilter,
    GaListView,
    AkButtonAction,
    AkDatatablePagination,
    GaView,
    AkInputSwitch,
  },
  mixins: [
    utilsMixin,
    sidebarMixin,
    companyMixin,
    poleMixin,
    roleMixin,
    filterListMixin,
    messageMixin,
    permissionsMixin,
    civilitiesConst,
    roleKeysConst,
  ],
  head() {
    return {
      title: 'employee.list',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      totalItems: 0,
      item: {},
      filter: {
        page: 1,
        limit: 20,
        sort: ['id:DESC'],
      },
    };
  },
  mounted() {},
  computed: {
    hasManagePermission() {
      return this.hasPermission(PermissionMap.RH.MANAGE);
    },
  },
  methods: {
    search() {
      this.loading = true;
      employeeService
        .employeesPagination(this.filter)
        .then((data) => {
          this.list = data.data;
          this.totalItems = data.totalItems;
        })
        .finally(() => (this.loading = false));
    },
    toggleCreateSidebar() {
      this.$nextTick(() => {
        this.$refs.employeeCreateSidebar.hide();
        this.search();
      });
    },
    openCreateSidebar() {
      if (this.$refs.employeeCreateSidebar) this.$refs.employeeCreateSidebar.show();
    },
    changeUserEnabled(data) {
      if (data.userEnabled) this.enableUser(data);
      else this.disableUser(data);
    },
    disableUser(data) {
      employeeService
        .disable(data)
        .then(() => {
          this.success(this.$t('user.disabled_success'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        });
    },
    enableUser(data) {
      employeeService
        .enable(data)
        .then(() => {
          this.success(this.$t('user.enabled_success'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        });
    },
    canDisable(data) {
      if (this.isMe(data.userId)) return false;
      return this.hasManagePermission;
    },
    resetFilter() {
      this.filter = {
        page: 1,
        limit: 20,
        sort: ['id:DESC'],
      };
      this.toggleFilterTitle(
        'GaEmployeeListFilter',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    openFilterPanel() {
      this.toggleFilterTitle(
        'GaEmployeeListFilter',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
  },
};
</script>
<template>
  <GaView>
    <template #action>
      <AkButtonAction v-if="hasManagePermission" :label="$t('add')" little @click="openCreateSidebar()" />
    </template>
    <template #content>
      <GaListView>
        <template #filter>
          <AkListFilter
            v-if="isGas()"
            v-model="filter.poleIds"
            :display-filter="true"
            :items="poles"
            :label="$t('team')"
            :multi-selection="true"
            direction="right"
            @search="search" />
          <AkInputFilter v-model="filter.name" :label="$t('interlocutor.salarie')" @search="search" />
          <AkListFilter
            v-model="filter.roleIds"
            :display-filter="true"
            :items="rolesWithLabel"
            :label="$t('employee.job')"
            :multi-selection="true"
            direction="right"
            item-display="label"
            item-value="value"
            @search="search" />
          <AkAllFilter @open="openFilterPanel" />
        </template>
        <template #content>
          <AkDatatablePagination
            :filter="filter"
            :loading="loading"
            :nb-items="totalItems"
            :value="list"
            @search="search">
            <template #empty>
              {{ $t('employee.empty') }}
            </template>
            <Column :header="$t('interlocutor.salarie')" field="last_name" header-style="width: 300px" sortable>
              <template #body="slotProps">
                <router-link
                  v-if="hasPermission('RH_VIEW')"
                  :to="{name: 'employeePage', params: {employeeId: slotProps.data.id}}">
                  {{ slotProps.data.lastName }}&nbsp;{{ slotProps.data.firstName }}
                </router-link>
                <template v-else>{{ slotProps.data.lastName }}&nbsp;{{ slotProps.data.firstName }}</template>
              </template>
            </Column>
            <Column :header="$t('employee.email')" field="email" header-style="width: 400px" sortable>
              <template #body="slotProps">
                {{ slotProps.data.email }}
              </template>
            </Column>
            <Column :header="$t('employee.roleKey')">
              <template #body="slotProps">
                {{ displayForRoleKeysConst(slotProps.data.roleKey) }}
              </template>
            </Column>
            <Column
              :header="$t('user.enabled')"
              body-style="text-align: center; overflow: visible"
              header-style=" width: 100px; text-align: center"
              sort-field="j_user_enabled"
              sortable>
              <template #body="slotProps">
                <AkInputSwitch
                  validationPopup
                  :nameOnPopup="slotProps.data.lastName + ' ' + slotProps.data.firstName"
                  :show-icon-popup="slotProps.data.userEnabled === true"
                  v-model="slotProps.data.userEnabled"
                  :disabled="!canDisable(slotProps.data)"
                  @change="changeUserEnabled(slotProps.data)" />
              </template>
            </Column>
            <Column header-style=" width: 100px;">
              <template #body="slotProps">
                <div class="flex items-center gap-2">
                  <router-link
                    v-if="hasPermission('RH_VIEW')"
                    :to="{name: 'employeePage', params: {employeeId: slotProps.data.id}}">
                    <i class="ga-icon ga-external text-2xl text-grey-900" />
                  </router-link>
                  <router-link
                    v-if="hasPermission('RH_MANAGE')"
                    :to="{name: 'employeeUpdate', params: {employeeId: slotProps.data.id}}">
                    <i class="ga-icon ga-edit text-2xl text-grey-900" />
                  </router-link>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <EmployeeCreateSidebar ref="employeeCreateSidebar" @creation="toggleCreateSidebar" />
    </template>
  </GaView>
</template>
